import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useQuery } from '@tanstack/react-query'

interface LinkedInReplyMetrics {
  totalReplies: number
  todayReplies: number
  monthToDateReplies: number
  positiveReplies: number
  autoReplies: number
  loading: boolean
  error: string | null
  refetch: () => void
}

interface UseLinkedInReplyMetricsOptions {
  clientFilter?: string | null
  timeFrame?: string
  customDateRange?: { start: string; end: string } | null
}

export function useLinkedInReplyMetrics(options: UseLinkedInReplyMetricsOptions = {}) {
  const { clientFilter, timeFrame, customDateRange } = options

  // Memoize the query key to prevent unnecessary refetches
  const queryKey = useMemo(() => ['linkedin-reply-metrics', clientFilter, timeFrame, customDateRange], [clientFilter, timeFrame, customDateRange])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      // Calculate date range based on timeFrame or customDateRange
      let startDate: string | null = null
      let endDate: string | null = null

      if (customDateRange) {
        startDate = customDateRange.start
        endDate = customDateRange.end
      } else if (timeFrame) {
        const now = new Date()
        switch (timeFrame) {
          case 'today':
            startDate = now.toISOString().split('T')[0]
            endDate = startDate
            break
          case 'yesterday':
            const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
            startDate = yesterday.toISOString().split('T')[0]
            endDate = startDate
            break
          case 'last_7_days':
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            startDate = sevenDaysAgo.toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
          case 'last_30_days':
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            startDate = thirtyDaysAgo.toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
          case 'this_month':
          case 'mtd':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
        }
      }

      // Get today's date in UK timezone for today's replies calculation
      const ukDate = new Date().toLocaleDateString('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split('/').reverse().join('-')

      const tomorrow = new Date(new Date(ukDate).getTime() + 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0]

      // Get month start date (first day of current month) in UK timezone
      const now = new Date()
      const ukNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }))
      const monthStart = new Date(ukNow.getFullYear(), ukNow.getMonth(), 1)
        .toISOString().split('T')[0]

      // Helper function to apply client filter via campaign variations
      const applyClientFilter = async (baseQuery: any) => {
        let filteredQuery = baseQuery
        
        if (clientFilter && clientFilter !== 'all') {
          // Resolve client_id via campaign variations logic
          // Get all LinkedIn variants for the client
          const { data: campaigns } = await supabase
            .from('campaigns')
            .select('id')
            .eq('client_id', clientFilter)
          
          if (campaigns && campaigns.length > 0) {
            const campaignIds = campaigns.map(c => c.id)
            
            // Get segments for these campaigns
            const { data: segments } = await supabase
              .from('ab_test_segments')
              .select('id')
              .in('campaign_id', campaignIds)
            
            if (segments && segments.length > 0) {
              const segmentIds = segments.map(s => s.id)
              
              // Get LinkedIn variants for these segments
              const { data: variants } = await supabase
                .from('ab_test_variants')
                .select('heyreach_campaign_id')
                .in('segment_id', segmentIds)
                .eq('channel', 'linkedin')
                .not('heyreach_campaign_id', 'is', null)
              
              if (variants && variants.length > 0) {
                const heyreachCampaignIds = variants
                  .map(v => v.heyreach_campaign_id)
                  .filter(Boolean) as string[]
                
                // Filter by heyreach campaign IDs
                filteredQuery = filteredQuery.in('campaign_id', heyreachCampaignIds)
              } else {
                // No LinkedIn variants for this client, return empty result
                filteredQuery = filteredQuery.eq('campaign_id', 'no-matches')
              }
            } else {
              // No segments for this client, return empty result
              filteredQuery = filteredQuery.eq('campaign_id', 'no-matches')
            }
          } else {
            // No campaigns for this client, return empty result
            filteredQuery = filteredQuery.eq('campaign_id', 'no-matches')
          }
        }
        
        return filteredQuery
      }

      // Base query for LinkedIn replies - only every_message_reply_received with campaign_id
      const baseQuery = supabase
        .from('heyreach_linkedin_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_type', 'every_message_reply_received')
        .not('campaign_id', 'is', null)

      // Parallel queries for better performance
      const [
        totalRepliesQuery,
        todayRepliesQuery,
        monthToDateRepliesQuery
      ] = await Promise.all([
        applyClientFilter(baseQuery),
        applyClientFilter(
          supabase
            .from('heyreach_linkedin_events')
            .select('*', { count: 'exact', head: true })
            .eq('event_type', 'every_message_reply_received')
            .not('campaign_id', 'is', null)
            .gte('created_at', ukDate)
            .lt('created_at', tomorrow)
        ),
        applyClientFilter(
          supabase
            .from('heyreach_linkedin_events')
            .select('*', { count: 'exact', head: true })
            .eq('event_type', 'every_message_reply_received')
            .not('campaign_id', 'is', null)
            .gte('created_at', monthStart)
            .lt('created_at', tomorrow)
        )
      ])

      const [totalRepliesResult, todayRepliesResult, monthToDateRepliesResult] = await Promise.all([
        totalRepliesQuery,
        todayRepliesQuery,
        monthToDateRepliesQuery
      ])

      // For positive and auto replies, we'll set to 0 for now (will be built later)
      // These would require AI categorization which is not yet implemented

      return {
        totalReplies: totalRepliesResult.count || 0,
        todayReplies: todayRepliesResult.count || 0,
        monthToDateReplies: monthToDateRepliesResult.count || 0,
        positiveReplies: 0, // Will be built later
        autoReplies: 0, // Will be built later
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
  })

  return {
    totalReplies: data?.totalReplies || 0,
    todayReplies: data?.todayReplies || 0,
    monthToDateReplies: data?.monthToDateReplies || 0,
    positiveReplies: data?.positiveReplies || 0,
    autoReplies: data?.autoReplies || 0,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unknown error') : null,
    refetch, // Expose refetch function so UI can force refresh
  }
}
